import { DataPath, DataType, ExprNode, ParseNode, Types, } from '@mode-switch/express';
import { Pipeline } from '../Pipeline';
export var DataTypes;
(function (DataTypes) {
    function make(catalog) {
        return (pipeline, node) => {
            switch (node.type) {
                case "NullLiteral":
                case "BooleanLiteral":
                case "HexLiteral":
                case "NumberLiteral":
                case "StringLiteral":
                case "RegExpLiteral":
                case "DateLiteral":
                    return Pipeline.DataTypes.set(pipeline, node, catalog.getLiteralType(node));
                case "Identifier":
                case "AccessIdentifier":
                case "AccessExpression":
                    return vAccess(pipeline, node);
                case "CaseExpression":
                    return vCase(pipeline, node);
                case "CaseWhen":
                    return vCaseWhen(pipeline, node);
                case "CaseElse":
                    return vCaseElse(pipeline, node);
                case "IfExpression":
                    return vIf(pipeline, node);
                case "IfBase":
                    return vIfBase(pipeline, node);
                case "ElseIf":
                    return vElseIf(pipeline, node);
                case "IfElse":
                    return vIfElse(pipeline, node);
                case "CallExpression":
                    return vCall(pipeline, catalog, node);
                case "UnaryExpression":
                    return vUnary(pipeline, catalog, node);
                case "BinaryExpression":
                    return vBinary(pipeline, catalog, node);
                case "LogicalExpression":
                    return vLogical(pipeline, node);
                default:
                    return pipeline;
            }
        };
    }
    DataTypes.make = make;
    function vAccess(pipeline, node) {
        const param = Pipeline.Params.get(pipeline, node);
        if (param != null) {
            return Pipeline.DataTypes.set(pipeline, node, param.formula.type);
        }
        const column = Pipeline.Columns.get(pipeline, node);
        if (column != null) {
            return Pipeline.DataTypes.set(pipeline, node, column.formula.type);
        }
        if (Pipeline.Calcs.get(pipeline, node) != null) {
            return pipeline;
        }
        const path = Pipeline.Paths.get(pipeline, node);
        if (path == null) {
            throw new Error(`Cannot determine data type of attribute at ${node.location}`);
        }
        else {
            throw new Error(`Cannot determine data type of attribute at path ${DataPath.format(path)}`);
        }
    }
    function vUnary(pipeline, catalog, node) {
        const operandType = Pipeline.DataTypes.get(pipeline, node.operand);
        if (operandType === null || operandType === undefined) {
            throw new Error(`Cannot resolve data type of expression`);
        }
        switch (node.operator) {
            case 'NOT':
                if (operandType.name === "BOOLEAN") {
                    return Pipeline.DataTypes.set(pipeline, node, DataType.BooleanType.make());
                }
                else {
                    throw new Error(`NOT is being called with ${operandType.name.toUpperCase()}. Did you mean BOOLEAN?`);
                }
            case '-':
                switch (operandType.name) {
                    case "FLOAT":
                    case "DECIMAL":
                    case "INTEGER": {
                        return Pipeline.DataTypes.set(pipeline, node, catalog.getNegatedType(operandType));
                    }
                    default: {
                        throw new Error(`Cannot negate ${operandType.name} value.`);
                    }
                }
            case '+':
                return Pipeline.DataTypes.set(pipeline, node, catalog.getPlussedType(operandType));
            default:
                return pipeline;
        }
    }
    function vBinary(pipeline, catalog, node) {
        const left = Pipeline.DataTypes.get(pipeline, node.left);
        const right = Pipeline.DataTypes.get(pipeline, node.right);
        if (left == null || right == null) {
            throw new Error(`Error resolving data type of expression`);
        }
        switch (node.operator) {
            case '+':
                return Pipeline.DataTypes.set(pipeline, node, catalog.getAddedType(left, right));
            case '/':
                return Pipeline.DataTypes.set(pipeline, node, catalog.getDividedType(left, right));
            case '-':
            case '*':
            case '%':
                return Pipeline.DataTypes.set(pipeline, node, catalog.getMathedType(left, right, node.operator));
            case '=':
            case '<>':
            case '<':
            case '<=':
            case '>':
            case '>=':
                catalog.validateRelationalType(left, right, node.operator);
                return Pipeline.DataTypes.set(pipeline, node, DataType.BooleanType.make());
            default:
                return pipeline;
        }
    }
    function vLogical(pipeline, node) {
        const left = Pipeline.DataTypes.get(pipeline, node.left);
        const right = Pipeline.DataTypes.get(pipeline, node.right);
        if (left == null || right == null) {
            throw new Error(`Error resolving data type of expression`);
        }
        switch (node.operator) {
            case 'AND':
            case 'OR':
                return Pipeline.DataTypes.set(pipeline, node, DataType.BooleanType.make());
            default:
                return pipeline;
        }
    }
    function vCase(pipeline, node) {
        const valueTypes = [];
        const resultTypes = [];
        let lastResultType = null;
        let checkResultTypes = false;
        const discriminatorType = Pipeline.DataTypes.get(pipeline, node.value);
        if (discriminatorType == null) {
            throw new Error(`Cannot determine type of case value at ${ParseNode.toString(node.value)}`);
        }
        valueTypes.push(discriminatorType);
        for (const caseNode of node.cases) {
            const valueType = Pipeline.DataTypes.get(pipeline, caseNode.value);
            if (valueType == null) {
                throw new Error(`Cannot determine type of case value at ${ParseNode.toString(caseNode.value)}`);
            }
            const resultType = Pipeline.DataTypes.get(pipeline, caseNode.result);
            if (resultType == null) {
                throw new Error(`Cannot determine type of case result at ${ParseNode.toString(caseNode.result)}`);
            }
            valueTypes.push(valueType);
            resultTypes.push(resultType);
            if (resultType.name !== "NULL") {
                if (lastResultType == null) {
                    lastResultType = resultType;
                }
                else if (DataType.equals(lastResultType, resultType)) {
                    lastResultType = resultType;
                }
                else {
                    checkResultTypes = true;
                }
            }
        }
        if (node.else != null) {
            const elseType = Pipeline.DataTypes.get(pipeline, node.else.result);
            if (elseType == null) {
                throw new Error(`Cannot determine type of case result at ${ParseNode.toString(node.else.result)}`);
            }
            resultTypes.push(elseType);
            if (elseType.name !== "NULL") {
                if (lastResultType == null) {
                    lastResultType = elseType;
                }
                else if (DataType.equals(lastResultType, elseType)) {
                    lastResultType = elseType;
                }
                else {
                    checkResultTypes = true;
                }
            }
        }
        {
            const isValidDataTypeCombination = Types.matchesOrIsNull(valueTypes, Types.isStringDataType) ||
                Types.matchesOrIsNull(valueTypes, Types.isDateLikeDataType) ||
                Types.matchesOrIsNull(valueTypes, Types.isNumericLikeDataType);
            if (!isValidDataTypeCombination) {
                const displayTypes = valueTypes.map((t) => t.name).join(', ');
                throw new Error(`All case statement values must be of the same type, ` +
                    `saw: ${displayTypes}`);
            }
        }
        if (checkResultTypes) {
            const allNumeric = Types.matchesOrIsNull(resultTypes, Types.isNumericDataType);
            const allDateLike = Types.matchesOrIsNull(resultTypes, Types.isDateLikeDataType);
            if (allNumeric) {
                lastResultType =
                    resultTypes.find(Types.isDecimalDataType) ||
                        resultTypes.find(Types.isFloatDataType) ||
                        resultTypes.find(Types.isIntegerDataType);
            }
            else if (allDateLike) {
                lastResultType = DataType.TimestampType.make();
            }
            else {
                const displayTypes = resultTypes.map((t) => t.name).join(', ');
                throw new Error(`All case statement results must be of the same type, ` +
                    `saw: ${displayTypes}`);
            }
        }
        if (lastResultType == null) {
            lastResultType = DataType.NullType.make();
        }
        if (lastResultType != null) {
            return Pipeline.DataTypes.set(pipeline, node, lastResultType);
        }
        else {
            throw new Error(`Cannot determine type of case at ${ParseNode.toString(node)}`);
        }
    }
    function vCaseWhen(pipeline, node) {
        const resultType = Pipeline.DataTypes.get(pipeline, node.result);
        if (resultType == null) {
            throw new Error(`Cannot determine type of case result ${ParseNode.toString(node.result)}`);
        }
        return Pipeline.DataTypes.set(pipeline, node, resultType);
    }
    function vCaseElse(pipeline, node) {
        const resultType = Pipeline.DataTypes.get(pipeline, node.result);
        if (resultType == null) {
            throw new Error(`Cannot determine type of case else result ${ParseNode.toString(node)}`);
        }
        return Pipeline.DataTypes.set(pipeline, node, resultType);
    }
    function vIf(pipeline, node) {
        const types = [];
        let lastType = null;
        let checkTypes = false;
        const baseType = Pipeline.DataTypes.get(pipeline, node.base);
        if (baseType === undefined) {
            throw new Error(`Cannot determine type of if result ${ParseNode.toString(node.base)}`);
        }
        types.push(baseType);
        if (baseType.name !== "NULL") {
            lastType = baseType;
        }
        for (const elseNode of node.elses) {
            const elseifType = Pipeline.DataTypes.get(pipeline, elseNode);
            if (elseifType === undefined) {
                throw new Error(`Cannot determine type of if result ${ParseNode.toString(elseNode)}`);
            }
            types.push(elseifType);
            if (elseifType.name !== "NULL") {
                if (lastType == null) {
                    lastType = elseifType;
                }
                else if (DataType.equals(lastType, elseifType)) {
                    lastType = elseifType;
                }
                else {
                    checkTypes = true;
                }
            }
        }
        if (node.else != null) {
            const elseType = Pipeline.DataTypes.get(pipeline, node.else);
            if (elseType === undefined) {
                throw new Error(`Cannot determine type of if result ${ParseNode.toString(node.else)}`);
            }
            types.push(elseType);
            if (elseType.name !== "NULL") {
                if (lastType == null) {
                    lastType = elseType;
                }
                else if (DataType.equals(lastType, elseType)) {
                    lastType = elseType;
                }
                else {
                    checkTypes = true;
                }
            }
        }
        if (checkTypes) {
            const resultTypes = types;
            const allNumeric = Types.matchesOrIsNull(resultTypes, Types.isNumericDataType);
            const allDateLike = Types.matchesOrIsNull(resultTypes, Types.isDateLikeDataType);
            if (allNumeric) {
                lastType =
                    resultTypes.find(Types.isDecimalDataType) ||
                        resultTypes.find(Types.isFloatDataType) ||
                        resultTypes.find(Types.isIntegerDataType);
            }
            else if (allDateLike) {
                lastType = DataType.TimestampType.make();
            }
            else {
                const displayTypes = resultTypes.map((t) => t.name).join(', ');
                throw new Error(`All if/else results must be of the same type, ` +
                    `saw: ${displayTypes}`);
            }
        }
        if (lastType == null) {
            lastType = DataType.NullType.make();
        }
        if (lastType != null) {
            return Pipeline.DataTypes.set(pipeline, node, lastType);
        }
        else {
            throw new Error(`Cannot determine type of if/else ${ParseNode.toString(node)}`);
        }
    }
    function vIfBase(pipeline, node) {
        const resultType = Pipeline.DataTypes.get(pipeline, node.result);
        if (resultType == null) {
            throw new Error(`Cannot determine type of if/else result ${ParseNode.toString(node.result)}`);
        }
        return Pipeline.DataTypes.set(pipeline, node, resultType);
    }
    function vElseIf(pipeline, node) {
        const resultType = Pipeline.DataTypes.get(pipeline, node.result);
        if (resultType == null) {
            throw new Error(`Cannot determine type of if/else result ${ParseNode.toString(node.result)}`);
        }
        return Pipeline.DataTypes.set(pipeline, node, resultType);
    }
    function vIfElse(pipeline, node) {
        const resultType = Pipeline.DataTypes.get(pipeline, node.result);
        if (resultType == null) {
            throw new Error(`Cannot determine type of if/else result ${ParseNode.toString(node.result)}`);
        }
        return Pipeline.DataTypes.set(pipeline, node, resultType);
    }
    function vCall(pipeline, catalog, node) {
        const argTypes = [];
        const argNodes = [];
        for (const arg of node.arguments) {
            const argNode = Pipeline.ExprNodes.get(pipeline, arg);
            const argType = Pipeline.DataTypes.get(pipeline, arg);
            if (argNode == null) {
                throw new Error(`Cannot resolve call argument ${argNode} to ${node.callee}`);
            }
            if (argType == null) {
                throw new Error(`Cannot determine type of argument ${arg} to ${node.callee}`);
            }
            argNodes.push(argNode);
            argTypes.push(argType);
        }
        const callName = ExprNode.CallName.make(node.callee);
        const type = catalog.getExprCallType(callName, argNodes, argTypes);
        if (type == null) {
            const argString = node.arguments.map((arg) => arg.type).join(',');
            throw new Error(`Cannot determine type of call ${node.callee}(${argString})`);
        }
        return Pipeline.DataTypes.set(pipeline, node, type);
    }
})(DataTypes || (DataTypes = {}));
